package one.healthium.profile.ui.update_initial_weight

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.models.UserProfileOptions
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel

internal class UpdateInitialWeightViewModel(
    private val userInteractor: UserInteractor,
    appPreferences: AppPreferences,
) : BaseViewModel() {
    private val _state = MutableStateFlow(UpdateInitialWeightUiState())

    val uiMessages = UiMessageManager()
    val events = EventProducer<UpdateInitialWeightEvent>()

    init {
        combine(
            userInteractor.observeUser(),
            appPreferences.isMetricFlow
        ) { userModel, isMetric ->
            _state.update { state ->
                state.copy(
                    userModel = userModel,
                    isMetric = isMetric,
                )
            }
        }.launch()
    }

    fun state(isTargetWeight: Boolean): StateFlow<UpdateInitialWeightUiState> {
        _state.update { state ->
            state.copy(
                isTargetWeight = isTargetWeight
            )
        }
        return _state.asStateFlow()
    }

    fun onWeightChanged(value: Float) {
        _state.update { state ->
            state.copy(updatedWeight = value)
        }
    }

    fun onSaveClicked() {
        if (_state.value.updatedWeight == null) {
            events.trigger(UpdateInitialWeightEvent.OnInitialWeightUpdated)
            return
        }

        launch {
            val options = if (_state.value.isTargetWeight) {
                UserProfileOptions(targetWeight = _state.value.updatedWeight)
            } else {
                UserProfileOptions(weight = _state.value.updatedWeight)
            }

            _state.update { state -> state.copy(loading = true) }
            try {
                userInteractor.updateUserProfile(options)
                events.trigger(UpdateInitialWeightEvent.OnInitialWeightUpdated)
            } catch (e: Exception) {
                Logger.e(e) { "Failed to update weight" }
                uiMessages.add(UiMessage.throwable(e))
            } finally {
                _state.update { state -> state.copy(loading = false) }
            }
        }
    }
}